/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: header/LicenseStatus.tsx
 🧩 Description: License status indicator for the header
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { Shield, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SimpleTooltip } from '../ui/SimpleTooltip';
import { api } from '../../lib/api';

interface LicenseStatusData {
    mode: 'community' | 'licensed';
    status: string;
    max_extensions: number;
    extensions_used: number;
    days_remaining: number | null;
}

export function LicenseStatusIndicator() {
    const navigate = useNavigate();
    const [status, setStatus] = useState<LicenseStatusData | null>(null);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                // Use the centralized API client which handles authentication and tenant headers
                const response = await api.get<LicenseStatusData>('/licensing/status');
                if (response.data) {
                    setStatus(response.data);
                }
            } catch (error) {
                // Silently fail - not critical
            }
        };

        fetchStatus();
        // Re-fetch every 5 minutes
        const interval = setInterval(fetchStatus, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    if (!status) return null;

    const isCommunity = status.mode === 'community';
    const isExpiringSoon = status.days_remaining !== null && status.days_remaining <= 7;
    const isOverLimit = status.extensions_used > status.max_extensions;

    // Don't show anything if everything is fine with a licensed version
    if (!isCommunity && !isExpiringSoon && !isOverLimit) {
        return null;
    }

    const getTooltipContent = () => {
        if (isOverLimit) {
            return `⚠️ Extensions over limit: ${status.extensions_used}/${status.max_extensions}`;
        }
        if (isExpiringSoon) {
            return `⚠️ License expires in ${status.days_remaining} days`;
        }
        if (isCommunity) {
            return `Community Edition: ${status.extensions_used}/${status.max_extensions} extensions`;
        }
        return 'License Status';
    };

    const getIconColor = () => {
        if (isOverLimit) return 'text-red-500';
        if (isExpiringSoon) return 'text-yellow-500';
        if (isCommunity) return 'text-blue-500';
        return 'text-green-500';
    };

    return (
        <SimpleTooltip content={getTooltipContent()} position="bottom">
            <button
                onClick={() => navigate('/admin/licensing')}
                className={`p-2 hover:bg-accent rounded-md transition-colors relative ${getIconColor()}`}
            >
                {isOverLimit || isExpiringSoon ? (
                    <AlertTriangle className="h-5 w-5" />
                ) : (
                    <Shield className="h-5 w-5" />
                )}
                {(isOverLimit || isExpiringSoon) && (
                    <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                )}
            </button>
        </SimpleTooltip>
    );
}
